import { useState, useEffect } from "react";

import toast from "react-hot-toast";

import { getTenant } from "../../../utils/tenantManager";

import {
  getScaleModelsApi,
  getScaleModelByIdApi,
  createScaleModelApi,
  updateScaleModelApi,
  deleteScaleModelApi,
  getScaleMakesApi,
  getUnitsApi,
  getScaleModelsByMakeApi,
  getModelCapacitiesApi,
} from "../../../services/operation/scale/scaleModelService";

export const useScaleModelViewModel = () => {
  const tenantId = getTenant();

  const [loading, setLoading] = useState(false);

  const [models, setModels] = useState<any[]>([]);

  const [model, setModel] = useState<any>(null);

  const [units, setUnits] = useState<any[]>([]);

  const [page, setPage] = useState(1);

  const [search, setSearch] = useState("");

  const [totalCount, setTotalCount] = useState(0);

  const [pageSize] = useState(10);

  // =========================
  // MAKE SEARCH DROPDOWN
  // =========================

  const loadMakeOptions = async (
    search: string,
    _loadedOptions: readonly any[],
    additional?: { page: number },
  ) => {
    const page = additional?.page || 1;

    const res = await getScaleMakesApi(page, 20, search, tenantId!);

    const raw = res?.data?.data || res?.data || [];

    return {
      options: raw.map((x: any) => ({
        value: x.id,

        label: x.name,

        makeName: x.name,
      })),

      hasMore: raw.length === 20,

      additional: {
        page: page + 1,
      },
    };
  };

  // =========================
  // UNITS
  // =========================

  const loadUnits = async () => {
    try {
      const res = await getUnitsApi(tenantId!);

      setUnits(res?.data?.data || res?.data || []);
    } catch {
      toast.error("Failed to load units");
    }
  };

  // =========================
  // MODELS
  // =========================

  const loadModels = async (
    customPage?: number,
    customPageSize?: number,
    customSearch?: string,
  ) => {
    try {
      setLoading(true);

      const currentPage = customPage ?? page;

      const currentPageSize = customPageSize ?? pageSize;

      const currentSearch = customSearch ?? search;

      const res = await getScaleModelsApi(
        currentPage,

        currentPageSize,

        currentSearch,

        tenantId!,
      );

      setModels(res?.data || []);

      setTotalCount(res?.totalCount || 0);

      return res;
    } catch {
      toast.error("Failed to load scale models");
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // GET ONE
  // =========================

  const getModel = async (id: string) => {
    try {
      const res = await getScaleModelByIdApi(id, tenantId!);

      setModel(res);

      return res;
    } catch {
      toast.error("Failed to load model");
    }
  };

  // =========================
  // CREATE
  // =========================

  const createModel = async (data: any) => {
    try {
      const payload = {
        id: data.id,

        name: data.name,

        calibrationValidPeriod: Number(data.calibrationValidPeriod),

        makeId: data.makeId,

        makeName: data.makeName || "",

        modelCapacities: data.modelCapacities.map((x: any) => ({
          capacity: Number(x.capacity),

          division: Number(x.division),

          capacityUnitId: x.capacityUnitId,

          capacityUnitName:
            units.find((u: any) => u.id === x.capacityUnitId)?.name || "",

          divisionUnitId: x.divisionUnitId,

          divisionUnitName:
            units.find((u: any) => u.id === x.divisionUnitId)?.name || "",

          tenantId,
        })),

        createdOn: new Date().toISOString(),

        tenantId,
      };

      const res = await createScaleModelApi(payload, tenantId!);

      toast.success("Model created successfully");

      return res;
    } catch (err: any) {
      console.log("API Error:", err?.response?.data);

      toast.error(err?.response?.data?.message || "Failed to create model");

      throw err;
    }
  };

  // =========================
  // UPDATE
  // =========================

  const updateModel = async (data: any) => {
    try {
      const payload = {
        ...data,

        tenantId,
      };

      const res = await updateScaleModelApi(payload);

      toast.success("Model updated successfully");

      return res;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to update model");

      throw err;
    }
  };

  // =========================
  // DELETE
  // =========================

  const deleteModel = async (id: string) => {
    try {
      await deleteScaleModelApi(id, tenantId!);

      toast.success("Model deleted successfully");

      await loadModels();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to delete model");

      throw err;
    }
  };

  // =========================
  // GET MODELS BY MAKE
  // =========================

  const getModelsByMake = async (makeId: string) => {
    try {
      const res = await getScaleModelsByMakeApi(makeId, tenantId!);

      return res?.data || res || [];
    } catch {
      toast.error("Failed to load models");

      return [];
    }
  };

  // =========================
  // GET CAPACITIES BY MODEL
  // =========================

  const getCapacitiesByModel = async (modelId: string) => {
    try {
      const res = await getModelCapacitiesApi(modelId, tenantId!);

      return res?.data || res || [];
    } catch {
      toast.error("Failed to load capacities");

      return [];
    }
  };

  // =========================
  // INITIAL LOAD
  // =========================

  useEffect(() => {
    loadUnits();
  }, []);

  useEffect(() => {
    loadModels(page, pageSize, search);
  }, [page, search]);

  return {
    models,
    model,

    units,

    loading,

    page,
    setPage,

    pageSize,

    totalCount,

    search,
    setSearch,

    loadModels,
    loadUnits,

    loadMakeOptions,

    getModel,

    getModelsByMake,
    getCapacitiesByModel,

    createModel,

    updateModel,

    deleteModel,
  };
};
